import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Package, Droplets, CheckCircle2, Paintbrush2, Shield, Banknote } from 'lucide-react';

const ProductCard = ({ 
  title, 
  icon: Icon, 
  totalAmount, 
  unit, 
  packaging, 
  optimal, 
  estimatedPrice,
  colorClass = 'text-accent', 
  delay = 0 
}) => {
  const { t, isRTL } = useLanguage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: "easeOut" }}
    >
      <Card className="mineral-card h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center bg-secondary', colorClass)}>
              <Icon className="w-4 h-4" />
            </div>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Total */}
          <motion.div 
            className="text-center p-5 rounded-2xl bg-secondary/50"
            whileHover={{ scale: 1.02 }}
          >
            <motion.p 
              className="text-4xl font-bold text-foreground"
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ delay: delay + 0.2, type: "spring" }}
            >
              {totalAmount.toLocaleString()}
              <span className="text-base font-normal text-muted-foreground ms-1">{unit}</span>
            </motion.p>
            {estimatedPrice > 0 && (
              <p className="text-sm text-accent font-medium mt-2">
                ~₪{estimatedPrice.toLocaleString()}
              </p>
            )}
          </motion.div>

          {/* Packaging Options */}
          <div className="space-y-2">
            {packaging.map((option, idx) => {
              const isOptimal = option.size === optimal.size;
              
              return (
                <motion.div
                  key={option.size}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: delay + 0.3 + (idx * 0.1) }}
                  className={cn(
                    'p-3 rounded-xl border transition-all',
                    isOptimal 
                      ? 'border-success/50 bg-success/5' 
                      : 'border-border/50 bg-card'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{option.label}</span>
                      {isOptimal && (
                        <Badge variant="outline" className="border-success/50 text-success bg-success/10 text-xs">
                          <CheckCircle2 className="w-3 h-3 me-1" />
                          {t('recommended')}
                        </Badge>
                      )}
                    </div>
                    <span className="font-bold text-lg">×{option.packsNeeded}</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                    <motion.div 
                      className={cn('h-full rounded-full', isOptimal ? 'bg-success' : 'bg-accent')}
                      initial={{ width: 0 }}
                      animate={{ width: `${100 - option.leftoverPercent}%` }}
                      transition={{ delay: delay + 0.5, duration: 0.8 }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('leftover')}: ~{option.leftover} {unit}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const CalculatorResults = ({ calculations, selectedPrimer }) => {
  const { t, isRTL } = useLanguage();

  if (!calculations) return null;

  return (
    <div className="space-y-8">
      {/* Results Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ProductCard
          title={isRTL ? selectedPrimer?.nameHe : selectedPrimer?.nameEn}
          icon={Paintbrush2}
          totalAmount={calculations.primer.totalGrams}
          unit={isRTL ? 'גרם' : 'g'}
          packaging={calculations.primer.packaging}
          optimal={calculations.primer.optimal}
          estimatedPrice={calculations.primer.estimatedPrice}
          colorClass="text-warning"
          delay={0}
        />

        <ProductCard
          title={isRTL ? calculations.microlit.productName : calculations.microlit.productNameEn}
          icon={Droplets}
          totalAmount={calculations.microlit.totalGrams}
          unit={isRTL ? 'גרם' : 'g'}
          packaging={calculations.microlit.packaging}
          optimal={calculations.microlit.optimal}
          estimatedPrice={calculations.microlit.estimatedPrice}
          colorClass="text-accent"
          delay={0.15}
        />

        {calculations.sealer && (
          <ProductCard
            title={isRTL ? 'סילר אקרוסיל' : 'Acrosil Sealer'}
            icon={Shield}
            totalAmount={calculations.sealer.totalMl}
            unit={isRTL ? 'מ"ל' : 'ml'}
            packaging={calculations.sealer.packaging}
            optimal={calculations.sealer.optimal}
            estimatedPrice={calculations.sealer.estimatedPrice}
            colorClass="text-success"
            delay={0.3}
          />
        )}
      </div>

      {/* Summary Stats */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-card p-6 flex flex-wrap items-center justify-center gap-8 text-center"
      >
        <div>
          <p className="text-sm text-muted-foreground mb-1">{t('areaTitle')}</p>
          <p className="text-2xl font-bold text-foreground">{calculations.area} <span className="text-base font-normal">{t('areaUnit')}</span></p>
        </div>
        <div className="h-12 w-px bg-border hidden sm:block" />
        <div>
          <p className="text-sm text-muted-foreground mb-1">{isRTL ? 'שכבות' : 'Layers'}</p>
          <p className="text-2xl font-bold text-foreground">{calculations.layers}</p>
        </div>
        <div className="h-12 w-px bg-border hidden sm:block" />
        <div>
          <p className="text-sm text-muted-foreground mb-1">{isRTL ? 'מחיר משוער' : 'Est. Price'}</p>
          <p className="text-2xl font-bold text-accent flex items-center justify-center gap-1">
            <Banknote className="w-5 h-5" />
            ₪{calculations.totalPrice.toLocaleString()}
          </p>
        </div>
      </motion.div>
    </div>
  );
};
